import { Component, OnInit } from '@angular/core';
import { DataGridTemplate, flatten } from '../../../shared/data-grid/data-grid.template';
import { FormGroup } from '@angular/forms';
import { CtwrRequestService } from '../ctwr.service';
import { HttpParams } from '@angular/common/http';
import { FilterableService } from '../../mesc/filterable-service';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { Router } from '@angular/router';
import { utilities } from '../../../../modules/utility';

@Component({
    selector: 'app-reporting',
    templateUrl: './reporting.component.html',
    styleUrls: ['./reporting.component.scss']
})
export class ReportingComponent extends DataGridTemplate implements OnInit {
    editForm: FormGroup;
    requestNumberService: FilterableService = new FilterableService();
    partNumberService: FilterableService = new FilterableService();
    ewoNumberService: FilterableService = new FilterableService();
    ppcrNumberService: FilterableService = new FilterableService();
    tcrNumberService: FilterableService = new FilterableService();
    originatorService: FilterableService = new FilterableService();
    programService: FilterableService = new FilterableService();
    processService: FilterableService = new FilterableService();
    requestStatusService: FilterableService = new FilterableService();
    partStatusService: FilterableService = new FilterableService();
    partTypeService: FilterableService = new FilterableService();
    locationService: FilterableService = new FilterableService();
    pte1Service: FilterableService = new FilterableService();
    pte2Service: FilterableService = new FilterableService();
    tmUserService: FilterableService = new FilterableService();
    designPhaseService: FilterableService = new FilterableService();
    authorizationTypeService: FilterableService = new FilterableService();
    priorityService: FilterableService = new FilterableService();
    refRequestService: FilterableService = new FilterableService();
    designCaService: FilterableService = new FilterableService();
    isPrimaryService: FilterableService = new FilterableService();

    isShowDialog = false;
    isSummaryLoading = false;
    statisticsReport: any;

    constructor(
        private readonly router: Router,
        private readonly requestService: CtwrRequestService,
    ) {
        super();
    }

    ngOnInit() {
        this.filterable = 'menu';
        this.sortable = true;
        this.isServerSideFiltering = true;
        this.controls.add = false;
        this.controls.edit = false;
        this.controls.delete = false;
        this.controls.excel = false;
        this.controls.configure = true;
        this.reorderable = true;
        this.resizable = true;

        this.controls.customToolBarButtons = this.customToolBarButtons;
    }


    /**
     * 
     * @param opts 
     */
    read(opts: any): Promise<any> {
        const _path = '/Reporting';
        const filters = this.getFilters();

        const params = this.getSearchParams(this.gridState);
        return this.requestService
            .search(filters, _path, true, params)
            .then(x => {
                x.RequestParts = this.mapRequestParts(x.RequestParts);

                this.setLookupData(x.ReportLookupData);
                return {
                    data: x.RequestParts,
                    total: x.RecordCount
                } as GridDataResult
            });
    }


    private mapRequestParts(data) {
        return data.map(r => {
            r.ModifiedDate = utilities.getLocalDateTime(r.ModifiedDate);
            r.SubmissionDate = utilities.getLocalDateTime(r.SubmissionDate);
            r.IsPrimary = String(r.IsPrimary);
            r.DesignCorrection = String(r.DesignCorrection);
            return r;
        });
    }


    private mapCurrency = (value: number) => {
        if (!value || isNaN(value)) value = 0;
        return `$${value.toFixed(2)}`;
    }



    /**
     * 
     * @param data 
     */
    private setLookupData(data) {
        if (!data) return;
        this.requestNumberService.next(data.RequestNumber);
        this.partNumberService.next(data.PartNumber);
        this.ewoNumberService.next(data.EWONumber);
        this.ppcrNumberService.next(data.PPCRNumber);
        this.tcrNumberService.next(data.TCRNumber);
        this.originatorService.next(data.OriginatorName);
        this.processService.next(data.ProcessName);
        this.programService.next(data.ProgramName);
        this.requestStatusService.next(data.RequestStatus);
        this.partStatusService.next(data.PartStatus);
        this.partTypeService.next(data.PartType);
        this.locationService.next(data.Location);
        this.pte1Service.next(data.PTE1UserName);
        this.pte2Service.next(data.PTE2UserName);
        this.tmUserService.next(data.TMUserName);
        this.designPhaseService.next(data.DesignPhaseName);
        this.authorizationTypeService.next(data.AuthorizationType);
        this.priorityService.next(data.Priority);
        this.refRequestService.next(data.RefRequests);
        this.designCaService.next(data.DesignCA.map(x => String(x)));
        if (data.IsPrimary) this.isPrimaryService.next(data.IsPrimary.map(x => String(x)));
    }


    /**
     * Construct HttpParams using the gridState 
     */
    private getSearchParams(state: any, isExport = false): HttpParams {
        let _sort: any = state.sort[0];
        if (!_sort || !_sort.dir) _sort = { dir: 'desc', field: 'RequestNumber' };
        const _pageNumber = (state.skip / state.take) + 1;
        let httpParams: HttpParams = new HttpParams()
            .set('orderBy', _sort.field)
            .set('sortOrder', _sort.dir)
            .set('export', isExport.toString());

        if (!this.isAll) {
            httpParams = httpParams
                .set('pageSize', state.take.toString())
                .set('pageNumber', _pageNumber.toString())
        }

        return httpParams;
    }


    /**
     * Map and return filters from gridState
     */
    private getFilters(isExport = false) {
        const _filters: any = {};
        const gridFilter = this.gridState.filter;
        const _propertyNames = this.getColumns(isExport);
        if (_propertyNames) _filters.PropertyNames = _propertyNames;

        flatten(gridFilter).map(f => {
            const _field: string = f.field;
            let _values = f.value.toString().split(',').map(x => x.trim());
            if (_field === 'Subject' ||
                _field === 'Detail' ||
                _field === 'EstimatedCostofChange' ||
                _field === 'EstimatedGrossSavings' ||
                _field === 'NetSavings') {
                _values = _values.join();
            }

            if (_filters[_field]) {
                _values.forEach(v => _filters[_field].push(v));
            } else if (_field.toLowerCase().includes('date') && f.operator) {
                const datePrefix = f.operator === 'gte' ? 'From' : 'To';
                _filters[`${datePrefix}${f.field}`] = f.value;
            } else {
                _filters[_field] = _values;
            }
        });
        return _filters;
    }


    private getColumns(isExport: boolean): any[] {
        return isExport ? this.columns.filter(c => c.visible).map(c => c.field) : null
    }


    handleExport = () => {
        const _path = '/Reporting';
        const d = new Date()
        const _fileType = '.xlsx';
        const _fileName = [
            'CTWR', 'Requests', d.getDate(), (d.getMonth() + 1), d.getFullYear()]
            .join('_') + _fileType;
        const _params = this.getSearchParams(this.gridState, true);

        this.requestService
            .export(this.getFilters(true), _path, _params)
            .then(response => {
                const nav = (window.navigator as any)
                const blob = new Blob([response], { type: "application/vnd.ms-excel" });
                if (typeof navigator !== "undefined" && nav.msSaveOrOpenBlob) {
                    nav.msSaveOrOpenBlob(blob, _fileName);
                }
                else {
                    const a = window.document.createElement("a");
                    a.href = window.URL.createObjectURL(blob);
                    a.download = _fileName;
                    document.body.appendChild(a)
                    a.click();
                }
            })
    }



    /**
     * Use router to navigate to Detail view for selected Request
     * If record is editable, launch the edit dialog instead
     * @param button - selected grid button
     * @param dataItem - json for selected row (Request)
     */
    onViewDetail = (button: any, dataItem: any) => {
        this.router.navigate(['/CTWR/Detail', dataItem.RequestNumber]);
    }


    create(parentEditDataItem: any): void { }
    edit(dataItem: any): void { }
    save(dataItem: any, isNew: boolean): Promise<any> { return Promise.resolve([]) }
    delete(dataItem: any): Promise<any> { return Promise.resolve([]) }

    /****************************************************************************
     * SUMMARY
     ***************************************************************************/

    /**
     * 
     */
    onSummary = (button: any, dataItem: any) => {
        this.updateSummary();
    }


    /**
     * 
     */
    close = () => {
        this.isShowDialog = false;
    }


    /**
     * 
     */
    updateSummary() {
        const _path = '/StatisticsReport';
        const params = new HttpParams();
        const _filters = this.getFilters();
        this.isSummaryLoading = true;
        this.requestService
            .search(_filters, _path, true, params)
            .then(x => this.updateSummaryData(x));
    }


    /**
     * 
     * @param data 
     */
    private updateSummaryData(data: any) {
        this.isSummaryLoading = false;
        try {
            let total = data.RequestReport.TotalCount;
            delete data.RequestReport.TotalCount;
            data.RequestReport = Object.keys(data.RequestReport)
                .map(k => { return { title: k, value: data.RequestReport[k] } });
            data.RequestReport.push({ title: 'TotalCount', value: total });
            total = data.PartReport.TotalCount;
            delete data.PartReport.TotalCount;
            data.PartReport = Object.keys(data.PartReport)
                .map(k => { return { title: k, value: data.PartReport[k] } });
            data.PartReport.push({ title: 'TotalCount', value: total });
            data.ReportParameters = Object.keys(data.ReportParameters)
                .map(k => {
                    if (k.includes('Date')) {
                        data.ReportParameters[k] = utilities.getLocalDateTime(data.ReportParameters[k], 'mediumDate');
                    }
                    return { title: k, value: data.ReportParameters[k] }
                });
        } catch (e) {

        } finally {
            this.statisticsReport = data;
            this.isShowDialog = true;
        }
    }


    /**
     * 
     */
    printSummary = (summary, filters) => {
        let popupWin;
        const body = filters.innerHTML + summary.innerHTML;
        popupWin = window.open('', '_blank');
        if (popupWin) {
            popupWin.document.open();
            popupWin.document.write(`
      <html>
        <head>
          <title>CTWR Reporting Summary</title>
          <style>
          .column {
              min-width: 360px;
              width: 50%;
              margin-top: 10px;
          }
          .flex-column {
              display: flex;
              flex-direction: column;
          }
          .flex-stretch {
              flex: 1 1 auto;
              width: 100%;
          }
          .summary-wrapper {
              display: inline-flex;
              flex-wrap: wrap;
              justify-content: space-between;
              margin-bottom: 10px;
          }
          .inline-flex {
              display: inline-flex;
              width: 100%;
          }
          .flex-wrap {
              flex-wrap: wrap;
          }
          .item {
              width: 80%;
              padding: 8px;
              padding-top: 4px;
              padding-bottom: 4px;
              line-height: 1.65em;
              white-space: nowrap;
              display: inline-flex;
              justify-content: space-between;
              border-bottom: solid inherit 2px;
              span {
                  font-size: 0.875em;
                  width: 140px;
              }
          }
          strong {
            margin-top: 2.0em;
          }
          </style>
        </head>
        <body onload="window.print();window.close()">${body}</body>
      </html>`
            );
            popupWin.document.close();
        }
    }


    customToolBarButtons = [{
        title: 'Export filtered requests to excel',
        text: 'Export to Excel',
        iconClass: 'fa fa-fw fa-file-excel-o',
        callback: this.handleExport,
    }, {
        title: 'Request Reporting Summary',
        text: 'Summary',
        primary: true,
        iconClass: 'fa fa-fw fa-file-text-o',
        callback: this.onSummary
    }]


    columns: any[] = [
        {
            field: "RequestNumber",
            title: "Request#",
            width: 160,
            template: {
                type: 'action-link',
                filter: 'multiselect',
                allowCustom: true,
                service: this.requestNumberService,
                callback: this.onViewDetail
            }
        },
        {
            field: 'ProcessName',
            title: 'Process',
            textField: 'ProcessName',
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                service: this.processService
            }
        },
        {
            field: 'Priority',
            title: 'Priority',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                service: this.priorityService
            }
        },
        {
            field: 'EwoNumber',
            title: 'EWO No.',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                allowCustom: true,
                service: this.ewoNumberService
            }
        },
        {
            field: 'PPCRNumber',
            title: 'PPCR No.',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                allowCustom: true,
                service: this.ppcrNumberService
            }
        },
        {
            field: 'ToolChangeRequest',
            title: 'TCR No.',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                allowCustom: true,
                service: this.tcrNumberService
            }
        },
        {
            field: 'EstimatedGrossSavings',
            title: 'Estimated Gross Savings',
            visible: false,
            filterable: false,
            sortable: true,
            width: 140
        },
        {
            field: 'EstimatedCostofChange',
            title: 'Estimated Cost of Change',
            visible: false,
            filterable: false,
            sortable: true,
            width: 140
        },
        {
            field: 'NetSavings',
            title: 'Net Savings',
            visible: false,
            filterable: false,
            sortable: true,
            width: 140
        },
        {
            field: 'DesignCorrection',
            title: 'Design Correction',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'combobox',
                service: this.designCaService
            }
        },
        {
            field: 'AuthorizationType',
            title: 'Authorization Type',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                service: this.authorizationTypeService
            }
        },
        {
            field: 'RequestStatus',
            title: 'Request Status',
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                service: this.requestStatusService
            }
        },
        {
            field: 'RefRequests',
            title: 'Referenced Requests',
            visible: false,
            width: 140,
            filterable: true,
            template: {
                type: 'multiselect',
                service: this.refRequestService
            }
        },
        {
            field: 'Subject',
            title: 'Short Description',
            width: 240,
        },
        {
            field: 'Originator',
            title: 'Originator',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                service: this.originatorService
            }
        },
        {
            field: 'TMUserName',
            title: 'Technical Manager',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                service: this.tmUserService
            }
        },
        {
            field: 'PTE1UserName',
            title: 'PTE 1',
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                service: this.pte1Service
            }
        },
        {
            field: 'PTE2UserName',
            title: 'PTE 2',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                service: this.pte2Service
            }
        },
        {
            field: 'SubmissionDate',
            title: 'Submission Date',
            template: {
                type: 'date'
            }
        },
        {
            field: 'ModifiedDate',
            title: 'Modified Date',
            visible: false,
            template: {
                type: 'date'
            }
        },
        {
            field: 'ProgramName',
            title: 'Program Name',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                service: this.programService
            }
        },
        {
            field: 'PartType',
            title: 'Part Type',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                service: this.partTypeService
            }
        },
        {
            field: 'PartNumber',
            title: 'Part Number',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                allowCustom: true,
                service: this.partNumberService
            }
        },
        {
            field: 'IsPrimary',
            title: 'Primary Part?',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'combobox',
                service: this.isPrimaryService
            }
        },
        {
            field: 'DesignPhase',
            title: 'Design Phase',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                service: this.designPhaseService
            }
        },
        {
            field: 'Location',
            title: 'Location',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                service: this.locationService
            }
        },
        {
            field: 'PartStatus',
            title: 'Part Status',
            visible: false,
            filterable: true,
            sortable: true,
            width: 140,
            template: {
                type: 'multiselect',
                service: this.partStatusService
            }
        },
    ]

}
